import React from 'react'
import { graphql } from 'relay-runtime'
import { useLazyLoadQuery } from 'react-relay'
import { Checkbox, FormControlLabel, Typography } from '@mui/material'
import { AdminRole, BrandAccessEntry } from '../roles'
import { RoleSelect } from '../components/RoleSelect'
import { BrandAccessSelectorQuery } from './__generated__/BrandAccessSelectorQuery.graphql'

const brandsQuery = graphql`
  query BrandAccessSelectorQuery($input: AdminListBrandsRequest!) {
    adminListBrands(input: $input) {
      data {
        id
        name
      }
    }
  }
`

interface BrandAccessSelectorProps {
  accountId: string
  value: BrandAccessEntry[]
  onChange: (value: BrandAccessEntry[]) => void
}

/**
 * Lets an admin grant a user access to specific brands within an account,
 * choosing one or more roles per brand.
 */
export const BrandAccessSelector = ({
  accountId,
  value,
  onChange,
}: BrandAccessSelectorProps) => {
  const data = useLazyLoadQuery<BrandAccessSelectorQuery>(
    brandsQuery,
    { input: { accountId, pageNumber: 0, pageSize: 200 } },
    { fetchPolicy: 'store-and-network' }
  )

  const brands = data.adminListBrands.data ?? []

  const entryFor = (brandId: string) =>
    value.find((entry) => entry.brandId === brandId)

  const toggleBrand = (brandId: string, checked: boolean) => {
    if (checked) {
      onChange([...value, { brandId, roles: ['EDITOR'] }])
    } else {
      onChange(value.filter((entry) => entry.brandId !== brandId))
    }
  }

  const setRoles = (brandId: string, roles: AdminRole[]) => {
    onChange(
      value.map((entry) =>
        entry.brandId === brandId ? { ...entry, roles } : entry
      )
    )
  }

  if (brands.length === 0) {
    return (
      <p className="py-2 text-sm text-gray-500">
        This account has no active brands.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-1 py-1">
      <Typography variant="subtitle2" color="primary">
        Brand Access
      </Typography>
      {brands.map((brand) => {
        const entry = entryFor(brand.id)
        const checked = entry != null
        return (
          <div key={brand.id} className="flex items-center justify-between gap-2">
            <FormControlLabel
              control={
                <Checkbox
                  checked={checked}
                  onChange={(event) =>
                    toggleBrand(brand.id, event.target.checked)
                  }
                />
              }
              label={brand.name}
            />
            {checked && (
              <RoleSelect
                size="small"
                value={entry!.roles}
                onChange={(roles) => setRoles(brand.id, roles)}
                sx={{ minWidth: 180 }}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

export default BrandAccessSelector
